import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import MatchReport from '../components/MatchReport';
import { downloadMatchReportPdf } from '../utils/matchReportPdf';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  UserPlus, Mail, Trash2, Check, X, FileText, Inbox, Users2, Briefcase, ChevronDown,
  Search, Link2, Send, Loader2, Sparkles, Download, ExternalLink, Phone, Linkedin, Calendar,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const scoreTone = (n) =>
  n >= 85 ? 'bg-green-50 text-green-700 border-green-200'
  : n >= 70 ? 'bg-blue-50 text-blue-700 border-blue-200'
  : n >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200'
  : 'bg-rose-50 text-rose-700 border-rose-200';

const MatchChip = ({ s }) => {
  if (s.matchStatus === 'scoring') return (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-500 inline-flex items-center gap-1" data-testid={`match-scoring-${s.id}`}>
      <Loader2 className="w-3 h-3 animate-spin" /> Scoring…
    </span>
  );
  if (s.matchStatus === 'done' && typeof s.matchScore === 'number') return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${scoreTone(s.matchScore)}`} data-testid={`match-score-${s.id}`}>
      <Sparkles className="w-3 h-3" /> {s.matchScore}% match
    </span>
  );
  if (s.matchStatus === 'failed') return <span className="text-[10px] text-slate-400" data-testid={`match-failed-${s.id}`}>No score</span>;
  return <span className="text-[10px] text-slate-300">No CV</span>;
};

const StatusBadge = ({ status }) => (
  status === 'pending'
    ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 flex-shrink-0">In progress</span>
    : <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${status === 'accepted' ? 'bg-green-50 text-green-700' : 'bg-rose-50 text-rose-700'}`}>{status === 'accepted' ? 'Accepted' : 'Rejected'}</span>
);

const RecruiterAgencies = () => {
  const { userProfile } = useAuth();
  const orgId = userProfile?.organizationId;
  const decidedBy = `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || userProfile?.email || '';
  const [tab, setTab] = useState('submissions');
  const [agents, setAgents] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewId, setReviewId] = useState(null);

  const [q, setQ] = useState('');
  const [fAgent, setFAgent] = useState('all');
  const [fStatus, setFStatus] = useState('all');

  const posTitle = (id) => positions.find((p) => p.id === id)?.title || 'role';

  const load = useCallback(async (silent = false) => {
    if (!orgId) return;
    if (!silent) setLoading(true);
    try {
      const [a, s, p] = await Promise.all([
        axios.get(`${API}/api/agencies/agents`, { params: { organizationId: orgId } }),
        axios.get(`${API}/api/agencies/submissions`, { params: { organizationId: orgId } }),
        axios.get(`${API}/api/positions/organization/${orgId}`),
      ]);
      setAgents(a.data?.agents || []);
      setSubmissions(s.data?.submissions || []);
      const list = p.data?.data || p.data || [];
      setPositions((Array.isArray(list) ? list : []).map((x) => ({ id: x.id, title: x.title, status: x.status })));
    } catch (e) { /* noop */ } finally { if (!silent) setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const scoring = submissions.some((s) => s.matchStatus === 'scoring');
    if (!scoring) return;
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [submissions, load]);

  const decide = async (sub, kind) => {
    setBusy(true);
    try {
      if (kind === 'accept') {
        await axios.post(`${API}/api/agencies/submissions/${sub.id}/accept`, { organizationId: orgId, decidedByName: decidedBy });
        toast.success('Candidate accepted — added to Candidates with their match score');
        setReviewId(null);
      } else {
        if (!feedback.trim()) { setBusy(false); return; }
        await axios.post(`${API}/api/agencies/submissions/${sub.id}/reject`, { organizationId: orgId, feedback: feedback.trim(), decidedByName: decidedBy });
        toast.success('Submission rejected with feedback');
        setReviewId(null);
      }
      setRejectFor(null); setFeedback('');
      load(true);
    } catch (e) { toast.error(e.response?.data?.message || 'Action failed'); } finally { setBusy(false); }
  };

  const rescore = async (sub) => {
    try {
      await axios.post(`${API}/api/agencies/submissions/${sub.id}/rescore`, { organizationId: orgId });
      toast.success('Re-scoring the CV…');
      load(true);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not start scoring'); }
  };

  const removeAgent = async (a) => {
    if (!window.confirm(`Remove ${a.firstName || a.email}? They will lose portal access.`)) return;
    try { await axios.delete(`${API}/api/agencies/agents/${a.id}`, { params: { organizationId: orgId } }); setAgents((prev) => prev.filter((x) => x.id !== a.id)); toast.success('Agent removed'); }
    catch (e) { toast.error('Could not remove agent'); }
  };

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return submissions.filter((s) => {
      if (fAgent !== 'all' && s.agentId !== fAgent) return false;
      if (fStatus !== 'all' && s.status !== fStatus) return false;
      if (qq) {
        const hay = `${s.candidate?.firstName || ''} ${s.candidate?.lastName || ''} ${s.candidate?.email || ''} ${s.positionTitle || ''} ${s.agentName || ''}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [submissions, q, fAgent, fStatus]);

  // Always group by agent — the simplest way to "see submissions by agent".
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((s) => {
      const key = s.agentId || 'unknown';
      if (!map.has(key)) map.set(key, { agentId: key, agentName: s.agentName || 'Agent', subs: [] });
      map.get(key).subs.push(s);
    });
    return Array.from(map.values()).sort((a, b) => a.agentName.localeCompare(b.agentName));
  }, [filtered]);

  const reviewing = submissions.find((s) => s.id === reviewId) || null;

  const pendingCount = submissions.filter((s) => s.status === 'pending').length;
  const tabs = [
    { label: `Submissions (${pendingCount})`, active: tab === 'submissions', onClick: () => setTab('submissions') },
    { label: `Agents (${agents.length})`, active: tab === 'agents', onClick: () => setTab('agents') },
  ];

  return (
    <RecruiterLayout title="Agencies" subtitle="Invite external agents and review the candidates they submit" tabs={tabs}>
      <div className="max-w-5xl mx-auto p-4 lg:p-6" data-testid="recruiter-agencies-page">
        {tab === 'submissions' && (
          <div data-testid="submissions-view">
            {/* simple toolbar: search + agent dropdown + status dropdown */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-5">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search candidates…" className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="sub-search" />
              </div>
              <select value={fAgent} onChange={(e) => setFAgent(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="filter-agent">
                <option value="all">All agents</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{`${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email}</option>)}
              </select>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="filter-status">
                <option value="all">All statuses</option>
                <option value="pending">In progress</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {loading ? (
              <p className="text-sm text-slate-400 py-16 text-center">Loading…</p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-400 flex flex-col items-center gap-2" data-testid="submissions-empty">
                <Inbox className="w-8 h-8 text-slate-300" />
                <p className="text-sm">{submissions.length === 0 ? 'No candidate submissions yet.' : 'No submissions match your filters.'}</p>
              </div>
            ) : (
              <div className="space-y-6" data-testid="submissions-list">
                {groups.map((g) => (
                  <div key={g.agentId} data-testid={`agent-group-${g.agentId}`}>
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[11px] font-bold">{(g.agentName || '?').charAt(0).toUpperCase()}</span>
                      <h3 className="text-sm font-bold text-slate-800">{g.agentName}</h3>
                      <span className="text-xs text-slate-400">· {g.subs.length} candidate{g.subs.length !== 1 ? 's' : ''}</span>
                    </div>
                    <Card className="border-slate-200 shadow-sm rounded-xl">
                      <CardContent className="p-1.5 sm:p-2">
                        <div className="divide-y divide-slate-100">
                          {g.subs.map((s) => <SubmissionRow key={s.id} s={s} onOpen={() => setReviewId(s.id)} />)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'agents' && (
          <>
            <div className="flex justify-end mb-4">
              <Button onClick={() => setInviteOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="invite-agent-btn">
                <UserPlus className="w-4 h-4 mr-1.5" /> Invite agent
              </Button>
            </div>
            <Card className="border-slate-200 shadow-sm rounded-xl">
              <CardContent className="p-2 sm:p-4">
                {loading ? <p className="text-sm text-slate-400 py-10 text-center">Loading…</p>
                : agents.length === 0 ? (
                  <div className="text-center py-14 text-slate-400 flex flex-col items-center gap-2" data-testid="agents-empty">
                    <span className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center"><Users2 className="w-6 h-6 text-slate-300" /></span>
                    <p className="text-sm">No agents yet. Invite an external agent to start receiving candidates.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100" data-testid="agents-list">
                    {agents.map((a) => (
                      <AgentRow key={a.id} a={a} positions={positions} orgId={orgId} invitedByName={decidedBy} posTitle={posTitle} onSaved={() => load(true)} onRemove={() => removeAgent(a)} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {inviteOpen && <InviteModal orgId={orgId} positions={positions} invitedByName={decidedBy} onClose={() => setInviteOpen(false)} onInvited={() => { setInviteOpen(false); load(true); }} />}

      {reviewing && (
        <ReviewPanel s={reviewing} orgId={orgId} busy={busy}
          onClose={() => setReviewId(null)}
          onAccept={() => decide(reviewing, 'accept')}
          onReject={() => { setRejectFor(reviewing); setFeedback(''); }}
          onRescore={() => rescore(reviewing)} />
      )}

      {rejectFor && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setRejectFor(null)} data-testid="reject-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-1">Reject submission</h3>
            <p className="text-sm text-slate-500 mb-3">Give the agent feedback on why <b>{rejectFor.candidate?.firstName} {rejectFor.candidate?.lastName}</b> isn't moving forward.</p>
            <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={4} placeholder="e.g. Not enough experience with Kubernetes for this role." className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="reject-feedback-input" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRejectFor(null)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
              <Button onClick={() => decide(rejectFor, 'reject')} disabled={busy || !feedback.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-confirm-btn">Reject with feedback</Button>
            </div>
          </div>
        </div>
      )}
    </RecruiterLayout>
  );
};

const SubmissionRow = ({ s, onOpen }) => (
  <button onClick={onOpen} className="w-full text-left py-2.5 px-2 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors" data-testid={`submission-row-${s.id}`}>
    <span className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">
      {(s.candidate?.firstName || '?').charAt(0).toUpperCase()}
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-slate-900 truncate">{s.candidate?.firstName} {s.candidate?.lastName}</p>
        <MatchChip s={s} />
      </div>
      <p className="text-xs text-slate-500 truncate"><Briefcase className="w-3 h-3 inline mr-1" />{s.positionTitle}</p>
    </div>
    <StatusBadge status={s.status} />
    <ChevronDown className="w-4 h-4 text-slate-300 -rotate-90 flex-shrink-0" />
  </button>
);

const DocLink = ({ name, url, testid }) => (
  <div className="flex items-center justify-between py-2" data-testid={testid}>
    <div className="flex items-center gap-2.5 min-w-0">
      <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
      <span className="text-sm text-slate-700 truncate">{name}</span>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      <a href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">preview <ExternalLink className="w-3 h-3" /></a>
      <a href={url} download className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">download <Download className="w-3 h-3" /></a>
    </div>
  </div>
);

const RecordSection = ({ title, children, right }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const ReviewPanel = ({ s, orgId, busy, onClose, onAccept, onReject, onRescore }) => {
  const r = s.matchReport || null;
  const reportRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const cvUrl = s.cvId ? `${API}/api/agencies/cv/${orgId}/${s.cvId}` : null;
  const cand = s.candidate || {};

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    setPdfBusy(true);
    try { await downloadMatchReportPdf(reportRef.current, `Match_Report_${cand.firstName || ''}_${cand.lastName || ''}.pdf`); }
    catch (e) { toast.error('Could not generate PDF'); } finally { setPdfBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="review-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-slate-50 h-full shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-start justify-between z-10">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-11 h-11 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold flex-shrink-0">{(cand.firstName || '?').charAt(0).toUpperCase()}</span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-slate-900 truncate">{cand.firstName} {cand.lastName}</h2>
                <MatchChip s={s} />
                <StatusBadge status={s.status} />
              </div>
              <p className="text-xs text-slate-500 mt-0.5"><Briefcase className="w-3 h-3 inline mr-1" />{s.positionTitle} · submitted by {s.agentName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700" data-testid="review-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Candidate details */}
          <RecordSection title="Candidate details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <Field icon={Mail} label="Email" value={cand.email} />
              <Field icon={Phone} label="Phone" value={cand.phone} />
              <Field icon={Linkedin} label="LinkedIn" value={cand.linkedin ? <a href={cand.linkedin} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{cand.linkedin}</a> : null} />
              <Field icon={Calendar} label="Submitted" value={s.createdAt ? new Date(s.createdAt).toLocaleDateString() : null} />
            </div>
            {cand.note && (
              <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-[11px] font-semibold text-slate-400 mb-1">Agent's note</p>
                <p className="text-sm text-slate-700">"{cand.note}"</p>
              </div>
            )}
          </RecordSection>

          {/* Documents */}
          <RecordSection title="Documents">
            {(!cvUrl && (s.documents || []).length === 0) ? (
              <p className="text-sm text-slate-400">No documents attached.</p>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="review-documents">
                {cvUrl && <DocLink name={s.cvName || 'CV'} url={cvUrl} testid="review-doc-cv" />}
                {(s.documents || []).map((d) => (
                  <DocLink key={d.id} name={d.name} url={`${API}/api/agencies/cv/${orgId}/${d.id}`} testid={`review-doc-${d.id}`} />
                ))}
              </div>
            )}
          </RecordSection>

          {/* Match report */}
          {s.matchStatus === 'scoring' && (
            <RecordSection title="AI match — only you see this">
              <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="review-match-scoring"><Loader2 className="w-4 h-4 animate-spin" /> Analysing the CV against this role…</div>
            </RecordSection>
          )}
          {s.matchStatus === 'failed' && (
            <RecordSection title="AI match — only you see this">
              <div className="flex items-center justify-between gap-3" data-testid="review-match-failed">
                <span className="text-sm text-slate-500">{s.cvId ? 'No match score yet for this CV.' : 'No CV, so there is nothing to score.'}</span>
                {s.cvId && (
                  <Button onClick={onRescore} disabled={busy} variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50 h-8 px-3 text-xs" data-testid="review-rescore">
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> Run match score
                  </Button>
                )}
              </div>
            </RecordSection>
          )}
          {s.matchStatus === 'done' && r && (
            <RecordSection
              title="AI match report — only you see this"
              right={
                <Button onClick={downloadPdf} disabled={pdfBusy} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 h-8 px-3 text-xs" data-testid="review-download-pdf">
                  {pdfBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Download PDF
                </Button>
              }
            >
              <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                <div ref={reportRef}>
                  <MatchReport
                    data={r}
                    candidateName={`${cand.firstName || ''} ${cand.lastName || ''}`.trim()}
                    candidateInfo={{ locationCity: cand.location }}
                    collapsed={false}
                  />
                </div>
              </div>
            </RecordSection>
          )}

          {s.status === 'rejected' && s.feedback && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700"><b>Feedback sent:</b> {s.feedback}</div>
          )}
          {s.status === 'accepted' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2"><Check className="w-4 h-4" /> Accepted — this candidate is in your Candidates pipeline.</div>
          )}
        </div>

        {s.status === 'pending' && (
          <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
            <Button onClick={onReject} disabled={busy} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="review-reject"><X className="w-4 h-4 mr-1" /> Reject</Button>
            <Button onClick={onAccept} disabled={busy} className="bg-green-600 hover:bg-green-700 text-white" data-testid="review-accept"><Check className="w-4 h-4 mr-1" /> Accept candidate</Button>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, value }) => (
  <div>
    <p className="text-[11px] font-semibold text-slate-400 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
    <p className="text-sm text-slate-700 break-words mt-0.5">{value || '—'}</p>
  </div>
);

const AgentRow = ({ a, positions, orgId, invitedByName, posTitle, onSaved, onRemove }) => {
  const [resending, setResending] = useState(false);
  const st = a.stats || { submitted: 0, accepted: 0, rejected: 0, pending: 0, acceptanceRate: null };

  const resend = async () => {
    setResending(true);
    try { await axios.post(`${API}/api/agencies/agents/${a.id}/resend`, { organizationId: orgId, invitedByName }); toast.success('Invite re-sent'); }
    catch (e) { toast.error('Could not resend invite'); } finally { setResending(false); }
  };
  const copyLink = async () => {
    if (!a.inviteUrl) return;
    try { await navigator.clipboard.writeText(a.inviteUrl); toast.success('Invite link copied'); }
    catch { toast.error('Could not copy link'); }
  };

  return (
    <div className="flex items-center gap-3 py-3 px-2 group" data-testid={`agent-row-${a.id}`}>
      <span className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">
        {(a.firstName || a.email || '?').charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900 truncate">{`${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email}</p>
        <p className="text-xs text-slate-400 truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {a.email}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          {(a.assignedRoleIds || []).map((rid) => (
            <span key={rid} className="text-[10px] bg-slate-50 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{posTitle(rid)}</span>
          ))}
          {(a.assignedRoleIds || []).length === 0 && <span className="text-[10px] text-slate-300">No roles assigned</span>}
        </div>
      </div>

      <div className="hidden md:flex flex-col items-end mr-1 flex-shrink-0" data-testid={`agent-stats-${a.id}`}>
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span title="Submitted"><b className="text-slate-800">{st.submitted}</b> sent</span>
          <span className="text-green-600" title="Accepted"><b>{st.accepted}</b> ✓</span>
          <span className="text-rose-500" title="Rejected"><b>{st.rejected}</b> ✕</span>
        </div>
        {st.acceptanceRate != null && (
          <div className="flex items-center gap-1.5 mt-1 w-28">
            <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${st.acceptanceRate}%` }} /></div>
            <span className="text-[10px] font-semibold text-slate-500">{st.acceptanceRate}%</span>
          </div>
        )}
      </div>

      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${a.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
        {a.status === 'active' ? 'Active' : 'Invited'}
      </span>

      {a.status !== 'active' && (
        <>
          <button onClick={resend} disabled={resending} className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50" data-testid={`agent-resend-${a.id}`} title="Resend invite">
            {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
          <button onClick={copyLink} className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50" data-testid={`agent-copy-${a.id}`} title="Copy invite link">
            <Link2 className="w-4 h-4" />
          </button>
        </>
      )}
      <AssignRoles agent={a} positions={positions} orgId={orgId} onSaved={onSaved} />
      <button onClick={onRemove} className="p-2 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50" data-testid={`agent-remove-${a.id}`} title="Remove">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

const AssignRoles = ({ agent, positions, orgId, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(agent.assignedRoleIds || []);
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    try { await axios.patch(`${API}/api/agencies/agents/${agent.id}`, { organizationId: orgId, assignedRoleIds: sel }); setOpen(false); onSaved(); toast.success('Roles updated'); }
    catch (e) { toast.error('Could not update roles'); }
  };
  return (
    <div className="relative">
      <button onClick={() => { setSel(agent.assignedRoleIds || []); setOpen((o) => !o); }} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 whitespace-nowrap" data-testid={`agent-assign-${agent.id}`}>
        Assign roles <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-20 w-64 bg-white border border-slate-200 rounded-xl shadow-lg p-2 max-h-64 overflow-y-auto">
          {positions.length === 0 ? <p className="text-xs text-slate-400 p-2">No roles yet.</p> : positions.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded-lg cursor-pointer text-sm">
              <input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggle(p.id)} className="rounded" />
              <span className="truncate">{p.title}</span>
            </label>
          ))}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 mt-1">
            <button onClick={() => setOpen(false)} className="text-xs text-slate-500 px-2 py-1">Cancel</button>
            <button onClick={save} className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg" data-testid={`agent-assign-save-${agent.id}`}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
};

const InviteModal = ({ orgId, positions, invitedByName, onClose, onInvited }) => {
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '' });
  const [roles, setRoles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/agencies/agents`, { organizationId: orgId, ...form, assignedRoleIds: roles, invitedByName });
      toast.success('Invite sent');
      onInvited();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not send invite'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="invite-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-900 mb-4">Invite an external agent</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="First name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="invite-firstname" />
            <input placeholder="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="invite-lastname" />
          </div>
          <input type="email" placeholder="agent@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="invite-email" />
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">Assign to roles</p>
            <div className="border border-slate-200 rounded-lg p-2 max-h-40 overflow-y-auto">
              {positions.length === 0 ? <p className="text-xs text-slate-400 p-1">No roles yet.</p> : positions.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-1 py-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                  <input type="checkbox" checked={roles.includes(p.id)} onChange={() => setRoles((r) => r.includes(p.id) ? r.filter((x) => x !== p.id) : [...r, p.id])} />
                  <span className="truncate">{p.title}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="invite-submit-btn">{busy ? 'Sending…' : 'Send invite'}</Button>
        </div>
      </div>
    </div>
  );
};

export default RecruiterAgencies;
